import { SuccessClientResponse } from '@orbis-systems/orbis-api-clients';
import { Account, GroupRtbs, Model, Portfolio, PositionType } from '@orbis-systems/orbis-api-middleware';
import { queryOptions } from '@tanstack/react-query';
import { orbis } from '../api';

export interface ModelMemberRealtimeBalancesQueryParams {
    modelId: 'models' | string;
}
export interface ModelListingQueryParams {
    loadQuotes: boolean;
    loadAccounts: boolean;
}

export interface ModelPortfolioOptions {
    combined?: boolean;
    currency?: string;
    symbol?: string;
    positionType?: PositionType;
}

export interface ModelPortfolioQueryParams {
    modelId: string;
    currency?: string;
    /**
     * Should load quotes or not.
     * @example "false"
     */
    loadQuotes?: boolean;
    /**
     * Whether to return combined portfolio (no accounts, with approximate prices)
     * @example "false"
     */
    combined?: boolean;
    /** @example "false" */
    loadIndustries?: boolean;
    /** @example "false" */
    loadUpgrades?: boolean;
    /** @example "false" */
    loadEarnings?: boolean;
    /**
     * Whether or not to include orders comprising each position's cost
     * @example "false"
     */
    loadCost?: boolean;
    /**
     * Analyze market caps and get `marketCapWeights`. To be used in conjuction with `loadQuotes`
     * @example "false"
     */
    marketCapWeights?: boolean;
}

export interface ModelMemberAccountStatisticsQueryParams {
    modelId: string;
}
export interface ModelMemberAccountsQueryParams {
    modelId: string;
}

export interface ModelMemberAccount {
    account: Account;
    model: Model;
}

export type MemberRealtimeBalancesResponse = SuccessClientResponse<GroupRtbs[]>;
export type ListingResponse = SuccessClientResponse<Model[]>;
export type PortfolioResponse = SuccessClientResponse<Portfolio>;
export type MemberAccountStatisticsResponse = SuccessClientResponse<Account[]>;
export type ModelMemberAccountsResponse = SuccessClientResponse<ModelMemberAccount[]>;

export const modelQueries = {
    all: () => queryOptions({ queryKey: ['models'] }),
    memberRealtimeBalances: (params: ModelMemberRealtimeBalancesQueryParams) =>
        queryOptions<MemberRealtimeBalancesResponse>({
            queryKey: [...modelQueries.all().queryKey, 'memberRealtimeBalances', params],
            queryFn: () => orbis.advisory.models.memberRealtimeBalances(params.modelId),
        }),
    listing: (params?: ModelListingQueryParams) =>
        queryOptions<ListingResponse>({
            queryKey: [...modelQueries.all().queryKey, 'listing', params],
            queryFn: () => orbis.advisory.models.listing(params),
        }),
    portfolio: (params: ModelPortfolioQueryParams) => {
        const { modelId, ...rest } = params;
        return queryOptions<PortfolioResponse>({
            queryKey: [...modelQueries.all().queryKey, 'portfolio', modelId, rest],
            queryFn: () => orbis.advisory.models.portfolio(modelId, rest),
        });
    },
    memberAccountStatistics: (params: ModelMemberAccountStatisticsQueryParams) =>
        queryOptions<MemberAccountStatisticsResponse>({
            queryKey: [...modelQueries.all().queryKey, 'memberAccountStatistics', params],
            queryFn: () => orbis.advisory.models.memberAccountStatistics(params.modelId),
        }),
    memberAccounts: (params: ModelMemberAccountsQueryParams) =>
        queryOptions<ModelMemberAccountsResponse>({
            queryKey: [...modelQueries.all().queryKey, 'memberAccounts', params],
            queryFn: () => orbis.advisory.models.memberAccounts(params.modelId),
        }),
};
